var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { memo, useMemo } from 'react';
import Alert from '@mui/material/Alert';
import CheckCircle from '@mui/icons-material/CheckCircle';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Error from '@mui/icons-material/Error';
import ReportProblem from '@mui/icons-material/ReportProblem';
import CustomIcon from '../Icon';
import { styled, useTheme } from '../styles';
import useTokens from '../utils/useTokens';
import { logErrorIfInvalidProp } from '../utils/errorHandling';
const CustomErrorIcon = memo(() => React.createElement(CustomIcon, { bdsIcon: Error, bdsSize: "small", bdsType: "errorNeutral" }));
const CustomSuccessIcon = memo(() => React.createElement(CustomIcon, { bdsIcon: CheckCircle, bdsSize: "small", bdsType: "successNeutral" }));
const CustomInfoIcon = memo(() => React.createElement(CustomIcon, { bdsIcon: InfoOutlined, bdsSize: "small", bdsType: "infoNeutral" }));
const CustomWarningIcon = memo(() => React.createElement(CustomIcon, { bdsIcon: ReportProblem, bdsSize: "small", bdsType: "warningNeutral" }));
const CustomInfoEmphasisIcon = memo(() => (React.createElement(CustomIcon, { bdsIcon: InfoOutlined, bdsSize: "small", sx: { color: useTokens().aliasesColorIconInfoSubtle } })));
// Function to get alert styles
const getAlertStyles = (theme, bdsType) => {
    switch (bdsType) {
        case 'neutralError':
            return {
                background: theme.palette.alert.neutral.error.background,
                border: `${theme.palette.alert.border.width} solid ${theme.palette.alert.neutral.error.border}`,
                color: theme.palette.alert.neutral.error.text,
                icon: React.createElement(CustomErrorIcon, null)
            };
        case 'neutralWarning':
            return {
                background: theme.palette.alert.neutral.warning.background,
                border: `${theme.palette.alert.border.width} solid ${theme.palette.alert.neutral.warning.border}`,
                color: theme.palette.alert.neutral.text,
                icon: React.createElement(CustomWarningIcon, null)
            };
        case 'neutralInfo':
            return {
                background: theme.palette.alert.neutral.info.background,
                border: `${theme.palette.alert.border.width} solid ${theme.palette.alert.neutral.info.border}`,
                color: theme.palette.alert.neutral.text,
                icon: React.createElement(CustomInfoIcon, null)
            };
        case 'neutralSuccess':
            return {
                background: theme.palette.alert.neutral.success.background,
                border: `${theme.palette.alert.border.width} solid ${theme.palette.alert.neutral.success.border}`,
                color: theme.palette.alert.neutral.text,
                icon: React.createElement(CustomSuccessIcon, null)
            };
        case 'emphasisInfo':
            return {
                background: theme.palette.alert.emphasis.info.background,
                border: `${theme.palette.alert.border.width} solid ${theme.palette.alert.emphasis.info.border}`,
                color: theme.palette.alert.emphasis.text,
                icon: React.createElement(CustomInfoEmphasisIcon, null)
            };
        default:
            logErrorIfInvalidProp('Alert', 'bdsType', bdsType);
            return undefined;
    }
};
const StyledAlert = styled(Alert, {
    shouldForwardProp: (prop) => !['bdsBoxShadowType', 'bdsType'].includes(prop)
})(({ theme, bdsBoxShadowType = 'inline', bdsType }) => {
    const alertStyles = getAlertStyles(theme, bdsType);
    const boxShadow = theme.palette.alert.boxShadow[bdsBoxShadowType];
    const numericValueOfSpaceBetween = parseFloat(theme.palette.alert.spaceBetween);
    const halfSpaceBetween = `${numericValueOfSpaceBetween / 2}px`;
    return Object.assign(Object.assign({ fontFamily: theme.palette.alert.font.family, fontSize: theme.palette.alert.font.size, fontWeight: theme.palette.alert.font.weight, lineHeight: theme.palette.alert.font.lineHeight, letterSpacing: theme.palette.alert.font.letterSpacing, borderWidth: theme.palette.alert.border.width, borderRadius: theme.palette.alert.border.radius, padding: theme.palette.alert.padding }, (alertStyles && {
        backgroundColor: alertStyles.background,
        border: alertStyles.border,
        color: alertStyles.color
    })), { boxShadow, '& .bds-alert-icon': {
            padding: `0 ${halfSpaceBetween} 0 0`,
            margin: 0,
            color: alertStyles === null || alertStyles === void 0 ? void 0 : alertStyles.color
        }, '& .bds-alert-message': {
            flexDirection: 'column',
            padding: `0 0 0 ${halfSpaceBetween}`,
            margin: 0,
            color: alertStyles === null || alertStyles === void 0 ? void 0 : alertStyles.color,
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '16px',
            overflow: 'hidden'
        } });
});
const BDSAlert = (_a) => {
    var { bdsType } = _a, props = __rest(_a, ["bdsType"]);
    const theme = useTheme();
    const alertStyles = useMemo(() => (bdsType ? getAlertStyles(theme, bdsType) : null), [theme, bdsType]);
    return (React.createElement(StyledAlert, Object.assign({ bdsType: bdsType, icon: alertStyles === null || alertStyles === void 0 ? void 0 : alertStyles.icon, classes: { icon: 'bds-alert-icon', message: 'bds-alert-message' } }, props)));
};
BDSAlert.displayName = 'Alert';
export default BDSAlert;
//# sourceMappingURL=Alert.js.map